import {
  Component,
  OnInit,
  OnDestroy,
  viewChild,
  inject,
  ChangeDetectionStrategy,
} from '@angular/core';
import {
  UntypedFormBuilder,
  UntypedFormGroup,
  Validators,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import { TranslateService, TranslatePipe } from '@ngx-translate/core';
import {
  ConfirmationService,
  FilterMetadata,
  PrimeTemplate,
} from '@openng/optimus-ui/api';
import { forkJoin, Subscription } from 'rxjs';
import { take } from 'rxjs/operators';
import {
  LazyLoadFilters,
  PageRequest,
  SearchContainerComponent,
} from 'src/app/core/containers/search-container/search-container.component';
import { TabService } from 'src/app/core/services/tab.service';
import { MonLogType } from 'src/app/model/admin/mon-log-type';
import { MonLogTypeInput } from 'src/app/model/admin/mon-log-type-input';
import { DirtyStatus } from 'src/app/model/common/dirty-status';
import { AdminBuilderService } from 'src/app/services/builder/admin-builder.service';
import { AdminApiService } from 'src/app/services/api/admin-api.service';
import { AuthApiService } from 'src/app/services/api/auth-api.service';
import { LogAndMessageService } from 'src/app/services/log-and-message.service';
import { MonlogsApiService } from 'src/app/services/api/monlogs-api.service';
import { MON_LOG_TYPES_TABLE_DEFINITION } from '../../definitions/data-table-definitions';
import { BusinessUnit } from 'src/app/model/admin/business-unit';
import { BusinessUnitFilterComponent } from 'src/app/core/components/business-unit-filter/business-unit-filter.component';
import { isNullOrUndefined } from 'src/app/utils/utils';
import { CLAIMS } from 'src/app/constants/auth-constants';
import { MonLogTypeTableService } from 'src/app/services/table/mon-log-type-table.service';
import { GridLazyLoadEvent } from 'src/app/core/components/base-grid.component';
import { CheckboxChangeEvent, Checkbox } from '@openng/optimus-ui/checkbox';
import { BlockUI } from '@openng/optimus-ui/blockui';
import { ProgressSpinnerComponent } from '../../../core/components/progress-spinner.component';
import { BusinessUnitFilterComponent as BusinessUnitFilterComponent_1 } from '../../../core/components/business-unit-filter/business-unit-filter.component';
import { ButtonDirective } from '@openng/optimus-ui/button';
import { GeneralGridComponent } from '../../../core/components/general-grid.component';
import { Dialog } from '@openng/optimus-ui/dialog';
import { InputText } from '@openng/optimus-ui/inputtext';
import { PickList } from '@openng/optimus-ui/picklist';

@Component({
  selector: 'app-mon-log-types-container',
  templateUrl: './mon-log-types-container.component.html',
  styleUrls: ['./mon-log-types-container.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    BlockUI,
    ProgressSpinnerComponent,
    BusinessUnitFilterComponent_1,
    ButtonDirective,
    GeneralGridComponent,
    Dialog,
    FormsModule,
    ReactiveFormsModule,
    InputText,
    Checkbox,
    PickList,
    PrimeTemplate,
    TranslatePipe,
  ],
})
export class MonLogTypesContainerComponent
  extends SearchContainerComponent<MonLogType>
  implements OnInit, OnDestroy
{
  protected _monLogApi = inject(MonlogsApiService);
  protected _adminBuilder = inject(AdminBuilderService);
  protected _translateService = inject(TranslateService);
  protected _logAndMessage = inject(LogAndMessageService);
  protected _tabService = inject(TabService);
  protected _monLogTypeTableService = inject(MonLogTypeTableService);
  protected _fb = inject(UntypedFormBuilder);
  protected _adminApi = inject(AdminApiService);
  protected _authApi = inject(AuthApiService);
  protected _confirmationService = inject(ConfirmationService);

  readonly businessUnitFilter =
    viewChild<BusinessUnitFilterComponent>('buFilter');

  canCreate = false;
  canEdit = false;
  saving = false;
  monLogTypeForm!: UntypedFormGroup;
  displayDialog = false;
  dialogHeader!: string;
  filteredContacts: any[] = [];

  types: MonLogType[] = [];
  selectedType?: MonLogType;
  showSuccessMessage = true;

  activeTabChangedSub!: Subscription;
  hasLoaded = false;

  availableBusinessUnits: BusinessUnit[] = [];
  sourcePicklist: BusinessUnit[] = [];
  targetPicklist: BusinessUnit[] = [];

  constructor() {
    super();
  }

  ngOnInit() {
    this._monLogTypeTableService.setLastLazyLoad(null);
    this.setTableService(this._monLogTypeTableService);
    this.setupActiveTabChangedSubscription();
    this.monLogTypeForm = this._fb.group({
      id: [null],
      name: [{ value: null, disabled: true }, Validators.required],
      enabled: [{ value: false, disabled: true }],
      version: [null],
      businessUnits: [{ value: null, disabled: false }],
      requiresCRER: [{ value: false, disabled: true }],
    });

    this.columns = MON_LOG_TYPES_TABLE_DEFINITION;
    const screenName$ = this._translateService.get(
      'MONLOG.SCREEN.MON_LOG_TYPES'
    );
    const dialogHeader$ = this._translateService.get(
      'MONLOG.LABEL.CALL_TYPE_DETAILS'
    );
    forkJoin([screenName$, dialogHeader$]).subscribe((label) => {
      this._tabService.updateActiveTabLabel(label[0]);
      this.screenName = label[0];
      this.dialogHeader = label[1];
    });

    this._adminApi.getAvailableBusinessUnits().subscribe((businessUnits) => {
      this.availableBusinessUnits = businessUnits;
    });

    this.canCreate = this._authApi.doesUserHaveAllClaimsFromList([
      CLAIMS.ADMINISTRATION.CALL_TYPES.CREATE_CALL_TYPES,
    ]);
    this.canEdit = this._authApi.doesUserHaveAllClaimsFromList([
      CLAIMS.ADMINISTRATION.CALL_TYPES.EDIT_CALL_TYPES,
    ]);
    if (this.canEdit || this.canCreate) {
      this.monLogTypeForm.enable();
    } else {
      this.monLogTypeForm.disable();
    }

    this.applyPreviousData();
    this.applyPreviousFilters();
  }

  ngOnDestroy(): void {
    if (this.activeTabChangedSub) {
      this.activeTabChangedSub.unsubscribe();
    }
  }

  override handleLazyLoad(
    req: PageRequest,
    $event: GridLazyLoadEvent,
    filters: LazyLoadFilters
  ) {
    this._monLogTypeTableService.setLastLazyLoad($event.lazy);
    const query: any = {
      name: null,
      enabled: null,
      businessUnits: null,
      businessUnitString: null,
      requiresCRER: null,
    };

    let selectedBusinessUnits = [];
    const businessUnitFilter = this.businessUnitFilter();
    if (businessUnitFilter?.selectedBusinessUnits) {
      // Handle further refreshes & changes to business unit filter component
      selectedBusinessUnits = businessUnitFilter.selectedBusinessUnits;
    } else {
      // Handle initial page load (before the business unit filter subcomponent exists)
      selectedBusinessUnits = this._authApi
        .getUserBusinessUnits()
        .map((bu) => bu.id);
    }
    query.businessUnits = selectedBusinessUnits;

    if (filters.name) {
      query.name = (filters.name as FilterMetadata).value;
    }

    if (
      filters.enabled &&
      !isNullOrUndefined((filters.enabled as FilterMetadata).value)
    ) {
      if ((filters.enabled as FilterMetadata).value) {
        query.enabled = 'true';
      } else {
        query.enabled = 'false';
      }
    }

    if (filters.businessUnitString) {
      query.businessUnitString = (
        filters.businessUnitString as FilterMetadata
      ).value;
    }

    if (filters.requiresCRER) {
      if ((filters.requiresCRER as FilterMetadata).value) {
        query.requiresCRER = 'true';
      } else {
        query.requiresCRER = 'false';
      }
    }

    this.queryNetwork(req, $event, query);
  }

  handleBusinessUnitChange() {
    this.lazyLoad({ lazy: this._monLogTypeTableService.getLastLazyLoad()! });
  }

  queryNetwork(
    req: PageRequest,
    $event: GridLazyLoadEvent,
    query: { [key: string]: any }
  ): void {
    req.page += 1;
    const sort = this.buildNetworkSortQuery($event, 'name');
    this._monLogApi
      .queryMonLogTypes(req.pageSize, req.page, query, sort)
      .subscribe({
        next: (connection) => {
          const items = connection.items;
          for (let i = 0; i < items.length; ++i) {
            let businessUnitsString = '';
            for (let j = 0; j < items[i].businessUnits.length; ++j) {
              businessUnitsString += items[i].businessUnits[j].name;
              if (j + 1 < items[i].businessUnits.length)
                businessUnitsString += ', ';
            }
            items[i].businessUnitString = businessUnitsString;
          }
          this.types = items;
          this.totalRecords = connection.totalRecords;
          this.elements = [...this.types];
          this.hasLoaded = true;
        },
        error: (error) => {
          this.loading = false;
          this._logAndMessage.errorLogOnly(error);
          this._logAndMessage.translateToErrorAlert({
            bodyKey: 'MONLOG.MESSAGES.SUCCESS.GET_MON_LOG_TYPE_ERROR',
            headerKey: 'COMMON.MESSAGES.HEADERS.ERROR',
          });
        },
        complete: () => {
          this.loading = false;
          if (this.showSuccessMessage) {
            this.showSuccessMessage = false;
            this._logAndMessage.translateToSuccessMessage({
              bodyKey: 'MONLOG.MESSAGES.SUCCESS.GET_MON_LOG_TYPE_SUCCESS',
              headerKey: 'COMMON.MESSAGES.HEADERS.SUCCESS',
            });
          }
        },
      });
  }

  changeViewEvent($event: { data: MonLogType }): void {
    this.selectedType = $event.data;
    this.monLogTypeForm.patchValue({
      id: $event.data.id,
      name: $event.data.name,
      enabled: $event.data.enabled,
      version: $event.data.version,
      businessUnits: $event.data.businessUnits,
      requiresCRER: $event.data.requiresCRER,
    });

    const businessUnits = this.monLogTypeForm.controls['businessUnits'].value;
    let currentBusinessUnits: string[] = [];
    businessUnits.forEach((x: BusinessUnit) => {
      currentBusinessUnits.push(x.name);
    });
    this.sourcePicklist = [];
    this.targetPicklist = [];
    this.availableBusinessUnits.forEach((x) => {
      if (currentBusinessUnits.includes(x.name)) {
        this.targetPicklist.push(x);
      } else {
        this.sourcePicklist.push(x);
      }
    });
    this.displayDialog = true;
  }

  clearForm(): void {
    this.monLogTypeForm.reset();
  }

  dialogClosed(): void {
    this.clearForm();
    this.selectedType = undefined;
  }

  newMonLogType(): void {
    this.monLogTypeForm.reset();
    this.monLogTypeForm.controls['enabled'].patchValue(false, {
      emitEvent: true,
    });
    this.sourcePicklist = [];
    this.targetPicklist = [];
    this.availableBusinessUnits.forEach((x) => {
      this.sourcePicklist.push(x);
    });
    this.displayDialog = true;
  }

  refresh(): void {
    this.lazyLoad({ lazy: this._monLogTypeTableService.getLastLazyLoad()! });
  }

  nameChanged(): void {
    if (this.selectedType) {
      this.selectedType.name = this.monLogTypeForm.controls['name'].value;
    }
  }

  toggleRecord($event: CheckboxChangeEvent, requiresCRER = false): void {
    if (requiresCRER) {
      if (this.selectedType) {
        this.selectedType.requiresCRER = $event.checked;
      }
    } else {
      if (this.selectedType) {
        this.selectedType.enabled = $event.checked;
      }
    }
  }

  saveMonLogType(): void {
    if (!this.canEdit && !this.canCreate) {
      return;
    }

    if (!isNullOrUndefined(this.monLogTypeForm) && this.monLogTypeForm.valid) {
      let callType = null;
      if (this.selectedType) {
        callType = this.selectedType;
        callType.businessUnits = this.targetPicklist;
      } else {
        callType = Object.assign({}, this.monLogTypeForm.getRawValue());
      }
      if (!callType.id) {
        this.createMonLogType(callType);
      } else {
        this.updateMonLogType(callType, true);
      }
    }
  }

  createMonLogType(callType: MonLogType) {
    if (!this.canCreate) {
      return;
    }

    const callTypeInput: MonLogTypeInput | null =
      this._adminBuilder.buildMonLogTypeInput(
        callType,
        true,
        this.targetPicklist
      );

    this.saving = true;
    this._monLogApi.createMonLogType(callTypeInput).subscribe({
      next: (result) => {
        this.clearForm();
        this.displayDialog = false;
      },
      error: (error) => {
        this.saving = false;
        this._logAndMessage.errorLogOnly(error);
        this._logAndMessage.translateToErrorAlert({
          bodyKey: 'MONLOG.MESSAGES.ERROR.CREATE_MON_LOG_TYPE',
          headerKey: 'COMMON.MESSAGES.HEADERS.ERROR',
        });
      },
      complete: () => {
        this.saving = false;
        this._logAndMessage.translateToSuccessMessage({
          bodyKey: 'MONLOG.MESSAGES.SUCCESS.CREATE_MON_LOG_TYPE',
          headerKey: 'COMMON.MESSAGES.HEADERS.SUCCESS',
        });
        this.refresh();
      },
    });
  }

  updateMonLogType(changes: MonLogType, closeDialog: boolean) {
    if (!this.canEdit) {
      return;
    }

    const callTypeChanges: MonLogTypeInput | null =
      this._adminBuilder.buildMonLogTypeInput(
        changes,
        false,
        this.targetPicklist
      );

    this.saving = true;
    this._monLogApi.updateMonLogType(changes.id, callTypeChanges).subscribe({
      next: (result) => {
        if (!closeDialog && !isNullOrUndefined(result)) {
          this.changeViewEvent({ data: result });
        } else {
          this.clearForm();
          this.displayDialog = false;
          this.refresh();
        }
      },
      error: (error) => {
        this.saving = false;
        this._logAndMessage.errorLogOnly(error);
        this._logAndMessage.translateToErrorAlert({
          bodyKey: 'MONLOG.MESSAGES.ERROR.UPDATE_MON_LOG_TYPE',
          bodyParams: { name: changes.name },
          headerKey: 'COMMON.MESSAGES.HEADERS.ERROR',
        });
      },
      complete: () => {
        this.saving = false;
        this._logAndMessage.translateToSuccessMessage({
          bodyKey: 'MONLOG.MESSAGES.SUCCESS.UPDATE_MON_LOG_TYPE',
          headerKey: 'COMMON.MESSAGES.HEADERS.SUCCESS',
        });
      },
    });
  }

  deleteMonLogType(): void {
    if (!this.canEdit) {
      return;
    }

    if (!isNullOrUndefined(this.monLogTypeForm) && this.monLogTypeForm.valid) {
      const callType = Object.assign({}, this.monLogTypeForm.getRawValue());
      let confHeader = null,
        ok = null,
        cancel = null,
        message = null;
      const confHeader$ = this._translateService.get(
        'MONLOG.MESSAGES.HEADERS.DELETE_CALL_TYPE'
      );
      const ok$ = this._translateService.get('COMMON.LABEL.BUTTONS.YES');
      const cancel$ = this._translateService.get('COMMON.LABEL.BUTTONS.NO');
      const message$ = this._translateService.get(
        'MONLOG.MESSAGES.CONFIRMATION.DELETE_CALL_TYPE',
        { name: callType.name }
      );

      forkJoin([confHeader$, ok$, cancel$, message$]).subscribe((messages) => {
        confHeader = messages[0];
        ok = messages[1];
        cancel = messages[2];
        message = messages[3];
      });

      this._confirmationService.confirm({
        header: confHeader!,
        icon: 'fa fa-question-circle',
        acceptVisible: true,
        rejectVisible: true,
        acceptLabel: ok!,
        rejectLabel: cancel!,
        message: message!,
        accept: () => {
          this.saving = true;
          if (!isNullOrUndefined(callType.id)) {
            // Existing call type so delete it
            this._monLogApi
              .allowMonLogTypeDelete(callType.id)
              .subscribe((canDelete) => {
                if (!canDelete) {
                  this.saving = false;
                  this._logAndMessage.translateToErrorAlert({
                    bodyKey: 'COMMON.MESSAGES.ERROR.DELETE_ERROR_MSG',
                    headerKey: 'COMMON.MESSAGES.HEADERS.ERROR',
                  });
                } else {
                  this._monLogApi.deleteMonLogType(callType.id).subscribe({
                    next: (result) => {
                      this.saving = false;
                      this.clearForm();
                      this.displayDialog = false;
                    },
                    error: (error) => {
                      this.saving = false;
                      this._logAndMessage.errorLogOnly(error);
                      this._logAndMessage.translateToErrorAlert({
                        bodyKey: 'MONLOG.MESSAGES.ERROR.DELETE_MON_LOG_TYPE',
                        bodyParams: { name: callType.name },
                        headerKey: 'COMMON.MESSAGES.HEADERS.ERROR',
                      });
                    },
                    complete: () => {
                      this._logAndMessage.translateToSuccessMessage({
                        bodyKey: 'MONLOG.MESSAGES.SUCCESS.DELETE_MON_LOG_TYPE',
                        bodyParams: { name: callType.name },
                        headerKey: 'COMMON.MESSAGES.HEADERS.SUCCESS',
                      });
                      this.refresh();
                    },
                  });
                }
              });
          } else {
            // new call type so just close dialog
            this.displayDialog = false;
          }
        },
        reject: () => {},
      });
    }
  }

  setupActiveTabChangedSubscription(): void {
    this.activeTabChangedSub = this._tabService.activeTabChanged.subscribe(
      (tab) => {
        if (!isNullOrUndefined(tab) && tab.header === this.screenName) {
          this.refresh();
        }
      }
    );
  }
}
